/**
 * Reports → Critical & Special Reports subpage.
 * Lists escalation and specialty forms with role-gated visibility.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, ChevronRight, ShieldAlert } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import {
  REPORT_TYPE_MAP,
  canCreateReport,
  type ReportType,
} from "@/lib/report-types";

export const Route = createFileRoute("/_authenticated/reports/critical")({
  component: CriticalReportsPage,
  head: () => ({
    meta: [{ title: "Critical & Special Reports — MissionReady" }],
  }),
});

const ITEMS: Array<{
  type: ReportType;
  title: string;
  subtitle: string;
  urgent?: boolean;
  supervisorOnly?: boolean;
}> = [
  {
    type: "company_car_accident",
    title: "Company Car Accident",
    subtitle: "Vehicle accident involving a company vehicle.",
  },
  {
    type: "equipment_damage",
    title: "Equipment Damage Report",
    subtitle: "Document damaged or destroyed equipment.",
  },
  {
    type: "maintenance_issue",
    title: "Maintenance Issue",
    subtitle: "Report a maintenance or facility issue.",
  },
  {
    type: "critical_incident",
    title: "Critical Incident Report",
    subtitle: "Escalated incidents requiring immediate supervisor notification.",
    urgent: true,
  },
  {
    type: "weapons_discharge",
    title: "Weapons Discharge Report",
    subtitle: "To be completed by site supervisor only.",
    supervisorOnly: true,
  },
];

function CriticalReportsPage() {
  const { currentRole } = useAuth();

  return (
    <div className="px-4 pb-28 pt-6 md:px-8 md:py-10">
      <div className="mx-auto max-w-3xl">
        <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2 text-muted-foreground hover:text-foreground">
          <Link to="/reports">
            <ArrowLeft className="mr-1 h-4 w-4" /> Reports
          </Link>
        </Button>

        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
          Critical & Special Reports
        </p>
        <h1 className="mt-1 font-display text-2xl font-bold md:text-3xl">
          Escalations & specialty forms
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          High-priority and restricted reports.
        </p>

        <ul className="mt-6 grid gap-3">
          {ITEMS.map((item) => {
            const meta = REPORT_TYPE_MAP[item.type];
            const Icon: LucideIcon = meta.icon;
            const allowed = canCreateReport(meta, currentRole);
            const Inner = (
              <div
                className={`group flex items-start gap-4 rounded-2xl border bg-card p-4 shadow-card transition-all ${
                  allowed
                    ? "border-[#C0C0C0]/15 hover:border-primary/50 active:scale-[0.99]"
                    : "border-dashed border-[#C0C0C0]/20 opacity-60"
                } ${item.urgent ? "ring-1 ring-primary/40" : ""}`}
              >
                <div
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset ring-[#C0C0C0]/15 ${
                    item.urgent ? "bg-primary/20 text-primary" : "bg-[#C0C0C0]/12 text-[#C0C0C0]"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-base font-semibold text-foreground">
                      {item.title}
                    </span>
                    {item.urgent && (
                      <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
                        Urgent
                      </span>
                    )}
                    {item.supervisorOnly && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#C0C0C0]/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#C0C0C0]">
                        <ShieldAlert className="h-3 w-3" /> Supervisor only
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    {item.subtitle}
                  </p>
                  {!allowed && (
                    <p className="mt-2 text-[11px] text-primary">
                      Requires supervisor or admin role.
                    </p>
                  )}
                </div>
                {allowed && (
                  <ChevronRight className="mt-2 h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                )}
              </div>
            );

            return (
              <li key={item.type}>
                {allowed ? (
                  <Link
                    to="/reports/new/$type"
                    params={{ type: item.type }}
                    className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    {Inner}
                  </Link>
                ) : (
                  <div aria-disabled="true">{Inner}</div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
